import { inject, Injectable } from '@angular/core';
import { BookmarkStore } from '../../stores/bookmark/bookmark.state';
import { StoryStore } from '../../stores/story/story.state';
import { BookmarkCategoryStore } from '../../stores/bookmark-category/bookmark-category.state';
import { Bookmark } from '../../models/bookmark';
import { BookmarkCategory } from '../../models/bookmark-category';
import { COLOR_TOKENS, ColorToken, Topic } from '../../models/topic';
import { Story } from '../../models/story';
import { Commune, LandingPageItem } from '../../models/commune';
import { TopicStore } from '../../stores/topic/topic.state';
import { CommuneStore } from '../../stores/commune/commune.state';
import { ActiveUserStore } from '../../stores/user/active-user.state';
import { HyperionContext } from '../../models/base-entity';
import { ThemeCssClass } from '../../components/atoms/theme-switcher/theme-switcher.component';
import { makeUniqueStringArray } from '../../utility/array.utility';

export interface TopicColorClasses {
  bg: string;
  border: string;
  text: string;
  bgStrong: string;
  textStrong: string;
}

@Injectable({
  providedIn: 'root',
})
export class TopicHelperService {
  readonly topicStore = inject(TopicStore);
  readonly bookmarkStore = inject(BookmarkStore);
  readonly storyStore = inject(StoryStore);
  readonly bookmarkCategoryStore = inject(BookmarkCategoryStore);
  readonly communeStore = inject(CommuneStore);
  readonly activeUserStore = inject(ActiveUserStore);

  constructor() {}

  /**
   * Retrieves a list of topics associated with a given bookmark.
   *
   * @param {Bookmark} bookmark - The bookmark object containing details to filter topics.
   * @return {Topic[]} An array of topics related to the specified bookmark. Returns an empty array if no category is found.
   */
  getTopicsByBookmark(bookmark: Bookmark): Topic[] {
    return bookmark.category
      ? this.getTopicsByBookmarkCategoryId(bookmark.category)
      : [];
  }

  /**
   * Picks the "primary" topic for a bookmark when its category happens to be
   * assigned to more than one topic: the one with the lowest `sorting` value -
   * the same admin-controlled order already used for the topic list/drag-and-drop
   * in `topic-administration`, so the pick is predictable rather than arbitrary.
   *
   * @param {Bookmark} bookmark - The bookmark to derive a topic for.
   * @return {Topic | null} The primary topic, or `null` if the bookmark has no category or no topic references it.
   */
  getPrimaryTopicForBookmark(bookmark: Bookmark): Topic | null {
    const topics = this.getTopicsByBookmark(bookmark);
    if (topics.length === 0) return null;
    return [...topics].sort((a, b) => a.sorting - b.sorting)[0];
  }

  /**
   * Retrieves a list of topics associated with a given story. If the story
   * has an associated category, topics are fetched based on the category ID.
   * Otherwise, an empty list is returned.
   *
   * @param {Story} story - The story object containing the category information.
   * @return {Topic[]} An array of topics related to the story or an empty array if no category is present.
   */
  getTopicsByStory(story: Story): Topic[] {
    return story.category
      ? this.getTopicsByBookmarkCategoryId(story.category)
      : [];
  }

  /**
   * Retrieves a list of topics associated with the specified bookmark category ID.
   *
   * @param {string} bookmarkCategory - The ID of the bookmark category to retrieve topics for.
   * @return {Topic[]} An array of topics related to the specified bookmark category.
   */
  getTopicsByBookmarkCategoryId(bookmarkCategory: string): Topic[] {
    return this.topicStore.findByBookmarkCategory(bookmarkCategory);
  }

  getTopicTags(topic: Topic) {
    const tagIds: string[] = [];
    topic.bookmarkCategories.forEach(category =>
      tagIds.push(...this.getBookmarkCategoryTags(category))
    );
    return makeUniqueStringArray(tagIds);
  }

  getBookmarkCategoryTags(bookmarkCategoryId: string) {
    const tagIds: string[] = [];
    this.bookmarkStore
      .getByCategoryId(bookmarkCategoryId)
      .forEach(bookmark => tagIds.push(...(bookmark.tags || [])));
    return makeUniqueStringArray(tagIds);
  }

  /**
   * Counts how many bookmark categories a topic has, and how many bookmarks/stories
   * live within those categories in total - a quick "how full is this topic" overview.
   *
   * @param {Topic} topic - The topic to compute stats for.
   * @return {{categoryCount: number; itemCount: number}} The category and item counts.
   */
  getTopicStats(topic: Topic): { categoryCount: number; itemCount: number } {
    const categoryIds = topic.bookmarkCategories || [];
    const groupedBookmarks = this.bookmarkStore.groupedByCategory();
    const groupedStories = this.storyStore.groupedByCategory();

    const itemCount = categoryIds.reduce(
      (count, categoryId) =>
        count +
        (groupedBookmarks[categoryId]?.length || 0) +
        (groupedStories[categoryId]?.length || 0),
      0
    );

    return { categoryCount: categoryIds.length, itemCount };
  }

  /**
   * Retrieves all bookmark categories that are not assigned to any topic.
   *
   * @return {BookmarkCategory[]} The bookmark categories not referenced by any topic's `bookmarkCategories`.
   */
  getUnassignedBookmarkCategories(): BookmarkCategory[] {
    const assignedIds = new Set(
      this.topicStore.sorted().flatMap(topic => topic.bookmarkCategories || [])
    );
    return this.bookmarkCategoryStore
      .asSortedArray()
      .filter(category => !assignedIds.has(category._id));
  }

  /**
   * Builds the PrimeFlex utility classes for a topic's color token, at the right
   * shade for the given theme. `bg`/`border`/`text` are the theme-aware, pale
   * shades meant for large ambient surfaces (menu bar, flyout panel) where
   * contrast against light/dark backgrounds matters; `bgStrong` is a fixed,
   * theme-independent accent shade (like `--primary-color` itself) meant for
   * small solid accents (icon badges, spotlight tiles, buttons).
   *
   * @param {ColorToken | undefined} token - The topic's color token, if any.
   * @param {ThemeCssClass} theme - The currently active theme.
   * @return {TopicColorClasses | null} The generated class names, or `null` if there's no valid token.
   */
  getTopicColorClasses(
    token: ColorToken | undefined,
    theme: ThemeCssClass
  ): TopicColorClasses | null {
    if (!token || !COLOR_TOKENS.includes(token)) return null;

    const isDark = theme === 'hyperion-dark';
    return {
      bg: `bg-${token}-${isDark ? '900' : '50'}`,
      border: `border-${token}-${isDark ? '700' : '300'}`,
      text: `text-${token}-${isDark ? '100' : '900'}`,
      bgStrong: `bg-${token}-500`,
      textStrong: `text-${token}-500`,
    };
  }

  /**
   * Resolves whether a topic is publicly visible, falling back to the legacy
   * `published` field for topics saved before the two-flag visibility model
   * existed (so older documents don't suddenly disappear).
   *
   * @param {Topic | undefined} topic - The topic to check.
   * @return {boolean} True if the topic should be publicly visible.
   */
  getPublicVisibility(topic: Topic | undefined): boolean {
    return (
      topic?.visibleInPublicAtlas ?? (topic as any)?.['published'] ?? false
    );
  }

  /**
   * Whether a topic should be shown given an Atlas context - `atlasExternal`/`guestView`
   * are treated as "public", `atlasInternal`/`dataBackend` as "internal".
   *
   * @param {Topic} topic - The topic to check.
   * @param {HyperionContext} [context] - The Atlas context to check against; defaults to the current user's context.
   * @return {boolean} True if the topic should be visible in that context.
   */
  isTopicVisibleForContext(topic: Topic, context?: HyperionContext): boolean {
    const activeContext = context || this.activeUserStore.context();
    const isPublicContext =
      activeContext === 'atlasExternal' || activeContext === 'guestView';

    if (isPublicContext) {
      return this.getPublicVisibility(topic);
    }
    return topic.visibleInInternalAtlas ?? true;
  }

  /**
   * Builds a virtual `Topic`-shaped object representing the "Fallback-Thema" setting
   * (a commune-level setting, not a stored Topic document) whose bookmark categories
   * are all categories not assigned to any real topic. Returns `null` when the setting
   * is hidden or there are currently no unassigned categories to show.
   *
   * @param {Commune | null} commune - The active commune to read the fallback-topic setting from.
   * @return {Topic | null} The virtual fallback topic, or `null` if it shouldn't be shown.
   */
  getFallbackTopic(commune: Commune | null): Topic | null {
    const config = commune?.atlasConfig?.fallbackTopic;
    if (!config?.visible) return null;

    const categories = this.getUnassignedBookmarkCategories();
    if (categories.length === 0) return null;

    return {
      _id: 'fallback',
      workspaces: [],
      title: config.title || 'Weitere Bereiche',
      description: config.description || '',
      icon: config.icon || 'layer-group',
      defaultBookmark: '',
      categories: [],
      bookmarkCategories: categories.map(category => category._id),
      visibleInPublicAtlas: true,
      visibleInInternalAtlas: true,
      sorting: -1,
      spotlight: config.spotlight || false,
    };
  }

  /**
   * Whether the given topic currently has an entry in the commune's curated
   * landing page items list.
   *
   * @param {Commune | null} commune - The active commune.
   * @param {string} topicId - The topic id to look up.
   * @return {boolean} True if the topic is shown on the landing page.
   */
  isOnLandingPage(commune: Commune | null, topicId: string): boolean {
    return !!commune?.atlasConfig?.landingPageItems?.some(
      item => item.type === 'Topic' && item.id === topicId
    );
  }

  /**
   * Adds or removes a topic from the commune's curated landing page items list
   * and persists the change. Shares the same `atlasConfig.landingPageItems`
   * array used by the "Startseite" configuration pick-list, so both stay in sync.
   *
   * @param {Commune} commune - The active commune (mutated and persisted in place).
   * @param {Topic} topic - The topic to show/hide on the landing page.
   * @param {boolean} visible - Whether the topic should appear on the landing page.
   * @return {void} Does not return a value.
   */
  setOnLandingPage(commune: Commune, topic: Topic, visible: boolean): void {
    const existingItems = commune.atlasConfig.landingPageItems || [];
    const withoutTopic = existingItems.filter(
      item => !(item.type === 'Topic' && item.id === topic._id)
    );

    const landingPageItems: LandingPageItem[] = visible
      ? [
          ...withoutTopic,
          {
            id: topic._id,
            name: topic.title,
            type: 'Topic',
            text: topic.description,
            icon: `fas fa-${topic.icon}`,
          },
        ]
      : withoutTopic;

    commune.atlasConfig = { ...commune.atlasConfig, landingPageItems };
    this.communeStore.update(commune);
  }
}
